/**
 * Bottom-sheet tray that surfaces every file the nurse has staged for a
 * compliance renewal — and lets her add more, remove one, or submit the
 * batch as a single combined upload.
 *
 * Mounted by `app/compliance/[docId].tsx` and rendered only when
 * `files.length > 0`. The submit button hands every staged file off to
 * the `/renew/combined` endpoint, which stitches them into one combined
 * PDF (or returns the lone file as-is on the single-file fast path).
 */
import { LinearGradient } from "expo-linear-gradient";
import {
    ActivityIndicator,
    Image,
    Modal,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Icon, Text } from "react-native-paper";

import { logRocketRedactProps } from "@/lib/logrocket";
import { colors, fonts, gradients, radius, shadows, spacing, typography } from "@/lib/theme";

export interface StagedRenewalFile {
    id: string;
    uri: string;
    contentType: string;
    filename: string;
}

interface RenewalStagingTrayProps {
    files: StagedRenewalFile[];
    uploading: boolean;
    onRemove: (id: string) => void;
    onSubmit: () => void;
    onCancel: () => void;
}

export function RenewalStagingTray({
    files,
    uploading,
    onRemove,
    onSubmit,
    onCancel
}: RenewalStagingTrayProps) {
    // Component is "always mounted" so React Native can animate the modal,
    // but we render nothing when there's nothing staged — keeps the screen
    // clean for the common single-file case.
    const visible = files.length > 0;
    const count = files.length;

    return (
        <Modal visible={visible} transparent animationType="slide">
            <View style={styles.overlay}>
                <View style={styles.sheet}>
                    <View style={styles.header}>
                        <Text style={styles.headerTitle}>
                            {count} {count === 1 ? "file" : "files"} staged
                        </Text>
                        {!uploading && (
                            <TouchableOpacity onPress={onCancel} hitSlop={12}>
                                <Icon source="close" size={24} color={colors.textSecondary} />
                            </TouchableOpacity>
                        )}
                    </View>

                    <Text style={styles.subhead}>
                        These will be combined into one document for review.
                    </Text>

                    <ScrollView
                        style={styles.scrollArea}
                        contentContainerStyle={styles.grid}
                        showsVerticalScrollIndicator={false}
                    >
                        {files.map((f) => (
                            <View key={f.id} style={styles.thumbWrap}>
                                {/*
                                 * PDFs can't render as <Image>. Use an icon
                                 * tile with the filename so the nurse can
                                 * still tell what's queued.
                                 */}
                                {f.contentType === "application/pdf" ? (
                                    <View style={[styles.thumb, styles.pdfThumb]}>
                                        <Icon
                                            source="file-pdf-box"
                                            size={32}
                                            color={colors.teal600}
                                        />
                                        <Text style={styles.pdfThumbLabel} numberOfLines={2}>
                                            {f.filename}
                                        </Text>
                                    </View>
                                ) : (
                                    <Image
                                        {...logRocketRedactProps()}
                                        source={{ uri: f.uri }}
                                        style={styles.thumb}
                                    />
                                )}
                                {!uploading && (
                                    <TouchableOpacity
                                        style={styles.removeBtn}
                                        onPress={() => onRemove(f.id)}
                                        hitSlop={8}
                                    >
                                        <Icon
                                            source="close-circle"
                                            size={22}
                                            color={colors.error}
                                        />
                                    </TouchableOpacity>
                                )}
                            </View>
                        ))}
                    </ScrollView>

                    {uploading ? (
                        <View style={styles.progressRow}>
                            <ActivityIndicator size="small" color={colors.teal400} />
                            <Text style={styles.progressText}>Combining + uploading…</Text>
                        </View>
                    ) : (
                        <>
                            <View style={styles.submitWrap}>
                                <TouchableOpacity onPress={onSubmit} activeOpacity={0.85}>
                                    <LinearGradient
                                        colors={[...gradients.teal]}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 1, y: 0 }}
                                        style={[styles.submitBtn, shadows.md]}
                                    >
                                        <Icon
                                            source="cloud-upload"
                                            size={20}
                                            color={colors.surface}
                                        />
                                        <Text style={styles.submitText}>
                                            Submit {count} {count === 1 ? "file" : "files"}
                                        </Text>
                                    </LinearGradient>
                                </TouchableOpacity>
                            </View>

                            <TouchableOpacity style={styles.cancelBtn} onPress={onCancel}>
                                <Text style={styles.cancelText}>Cancel</Text>
                            </TouchableOpacity>
                        </>
                    )}
                </View>
            </View>
        </Modal>
    );
}

const THUMB_SIZE = 96;

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.5)",
        justifyContent: "flex-end"
    },
    sheet: {
        backgroundColor: colors.surface,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        maxHeight: "78%",
        paddingTop: spacing.base,
        paddingBottom: 34,
        paddingHorizontal: spacing.base
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingBottom: spacing.sm
    },
    headerTitle: {
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    subhead: {
        ...typography.bodySmall,
        color: colors.textSecondary,
        marginBottom: spacing.md
    },
    scrollArea: {
        maxHeight: 240
    },
    grid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: spacing.sm,
        paddingBottom: spacing.md
    },
    thumbWrap: {
        position: "relative"
    },
    thumb: {
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: radius.md,
        backgroundColor: colors.backgroundSecondary
    },
    pdfThumb: {
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        padding: 6,
        borderWidth: 1,
        borderColor: colors.teal100
    },
    pdfThumbLabel: {
        ...typography.caption,
        color: colors.textSecondary,
        textAlign: "center"
    },
    removeBtn: {
        position: "absolute",
        top: -6,
        right: -6,
        backgroundColor: colors.surface,
        borderRadius: 11
    },
    progressRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        paddingVertical: spacing.lg
    },
    progressText: {
        ...typography.body,
        color: colors.textSecondary
    },
    submitWrap: {
        marginBottom: spacing.sm
    },
    submitBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        borderRadius: radius.full,
        paddingVertical: 14
    },
    submitText: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.surface
    },
    cancelBtn: {
        alignItems: "center",
        paddingVertical: spacing.sm
    },
    cancelText: {
        ...typography.body,
        color: colors.textSecondary
    }
});
